import useSWR from "swr"
import type {
	IHotelMealPlan,
	IHotelRoomType,
	THotelOtaRatePlan,
	THotelPaxTypeCode,
} from "../Hotels"
import { TMoney } from "@sembark-travel/money"
import { TChildrenArray } from "../Tourists"
import { useXHR } from "@sembark-travel/xhr"
import { TPricingType, TRateSheet } from "../RateSheets"
import { THotelOtaRoomType, TOtaRoomTypeWithMedia } from "../HotelOtaRoomTypes"
import { THotelOtaMealPlan } from "../HotelOtaMealPlans"
import { TPaymentPolicy } from "../PaymentPolicy"
import { TCancellationPolicy } from "../CancellationPolicy"

export type THotelRatePricingType = TPricingType
export type THotelRateSheet = TRateSheet
export type THotelPaymentPolicy = TPaymentPolicy
export type THotelCancellationPolicy = TCancellationPolicy

export interface IAvailabilityRate {
	pax_type: THotelPaxTypeCode
	// BE-defined display/matching label, e.g. "BASE", "AWEB", "CWEB (6-8)",
	// "CNB (2-5)", "A1", "A2". Age-band matching for CWEB/CNB should still use
	// start_age/end_age; `occupancy` is for display.
	occupancy: string
	pax_count: number
	start_age: number | null
	end_age: number | null
	rate_before_tax: string
	rate_before_tax_money: TMoney
	rack_rate_before_tax?: string | null
	rack_rate_before_tax_money?: TMoney | null
}

export interface IAvailabilityDate {
	date: string
	date_local: string
	available_rooms: number
	pricing_type: THotelRatePricingType
	// Exactly one of these is present — which one can vary night-to-night
	// (e.g. a DMC rate sheet not covering every night falls back to the
	// hotel's own rate plan for the uncovered nights).
	rate_sheet?: THotelRateSheet
	rate_plan?: THotelOtaRatePlan
	rates: IAvailabilityRate[]
}

export interface IHotelRemoteRoomType extends IHotelRoomType {
	ota_room_type: THotelOtaRoomType &
		Pick<TOtaRoomTypeWithMedia, "thumb_image_url">
}

export interface IHotelRemoteMealPlan extends IHotelMealPlan {
	ota_meal_plan: THotelOtaMealPlan
}

export interface IAvailabilityRateOption {
	meal_plan: IHotelRemoteMealPlan
	payment_policy: THotelPaymentPolicy
	cancellation_policy: THotelCancellationPolicy
	// BE-computed label for the rate-option selector
	name: string
	dates: IAvailabilityDate[]
	disabled: boolean
	// sent when `disabled` is true
	unavailable_reason?: string | null
}

export interface IAvailabilityRoomWiseRateOptions {
	room_type: IHotelRemoteRoomType
	rate_options: IAvailabilityRateOption[]
}

export interface IChildSlotOption {
	id: number
	age: number
	name: string
	// UI-only: set when this option can't be selected right now (e.g. base
	// pax/room not filled yet, or occupancy budget exhausted) so it renders
	// disabled with an explanatory hover title instead of silently failing.
	disabled?: boolean
	title?: string
}

export interface IAvailability {
	hotel_id: number
	dates: string[]
	room_type_wise_rates: IAvailabilityRoomWiseRateOptions[]
}

export type TChildSlotOptions = {
	[K in Extract<THotelPaxTypeCode, "CWEB" | "CNB" | "COMP">]: IChildSlotOption[]
}

export function useHotelAvailability(
	hotelId: number | undefined,
	dates: Array<string>,
	tripChildren: TChildrenArray | null | undefined,
	tripId: number | undefined
) {
	const xhr = useXHR()
	const childrenKey = (tripChildren || [])
		.map((c) => `${c.age}:${c.count}`)
		.join(",")
	const key =
		hotelId && dates.length && tripId
			? `/hotels/${hotelId}/ari/${dates.join(",")}/${childrenKey}/${tripId}`
			: null
	return useSWR<{
		data: IAvailability
		meta: {
			child_slot_options: TChildSlotOptions
		}
	}>(
		key,
		async () => {
			return xhr
				.get<{
					data: IAvailability
					meta: {
						child_slot_options: TChildSlotOptions
					}
				}>(`/hotels/${hotelId}/ari`, {
					params: {
						trip: tripId,
						dates_local: dates,
						children: tripChildren || [],
					},
				})
				.then((r) => {
					return {
						data: r.data.data,
						meta: r.data.meta,
					}
				})
		},
		{ revalidateOnFocus: false }
	)
}
